// İSG uzmanı portalı — Rol yönetimi (api-contract §9.1). Cross-tenant rol listesi
// (UTable sözleşmesi: page/pageSize/search + total/filteredTotal), oluştur/düzenle
// (RoleFormModal), soft-delete (pasifleştir). Sistem-admin rolü kilitli (FE önden
// engelle); "Yetkiler" → matris ekranı (§9.3).

import {
  Alert,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  Pagination,
  type RoleDto,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLink,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  Tag,
  adminApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, SearchX, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoleFormModal } from '../../components/admin/RoleFormModal';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { activeTagColor } from '../../components/status/statusTagColors';
import { adminKeys } from '../../lib/adminKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;

export function RolesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleDto | null>(null);
  const [deleteRole, setDeleteRole] = useState<RoleDto | null>(null);

  // Arama değişince ilk sayfaya dön (efekt yerine kaynağında).
  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    sortBy: 'name',
    sortDir: 'asc' as const,
  };

  const query = useQuery({
    queryKey: adminKeys.roles(params),
    queryFn: () => adminApi.listRoles(params),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => adminApi.deleteRole(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
      setMessage(t('admin.roles.deleted'));
      setDeleteRole(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch);
  function clearFilters() {
    setSearch('');
    setPage(1);
  }

  function scopeLabel(role: RoleDto): string {
    if (role.companyId != null) {
      return t('admin.roles.scope.company', { id: role.companyId });
    }
    return t('admin.roles.scope.global');
  }

  return (
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('admin.roles.new')}
        </Button>
      </ShellHeaderAction>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data ? t('admin.roles.count', { count: query.data.filteredTotal }) : undefined
            }
            search={search}
            onSearchChange={onSearchChange}
            searchLabel={t('admin.common.search')}
            searchPlaceholder={t('admin.roles.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
          />

          <Table caption={t('admin.roles.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.roles.col.name')}</TableHead>
                <TableHead>{t('admin.roles.col.value')}</TableHead>
                <TableHead>{t('admin.roles.col.scope')}</TableHead>
                <TableHead>{t('admin.roles.col.status')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('admin.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={5} />
              ) : query.isError ? (
                <TableMessageRow colSpan={5}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={5}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('admin.common.empty')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={ShieldCheck}
                      title={t('admin.common.noData')}
                      description={t('admin.common.noData.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('admin.roles.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((role) => (
                  <TableRow key={role.uuid}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {/* Birincil "aç" = ad hücresi (S33 Slice 4 QA fix, defekt #4) — eskiden
                            ayrı bir "Yetkiler" düğmesiydi, hedefi gerçek bir rota (izin
                            matrisi). */}
                        <TableLink to={`/admin/roles/${role.uuid}`}>{role.name}</TableLink>
                        {role.isSystemAdmin ? (
                          <Tag color="blue">{t('admin.roles.badge.systemAdmin')}</Tag>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="rounded bg-surface-muted px-1.5 py-0.5 text-xs text-muted">
                        {role.value}
                      </code>
                    </TableCell>
                    <TableCell className="text-muted">{scopeLabel(role)}</TableCell>
                    <TableCell>
                      <Tag color={activeTagColor(role.isActive)}>
                        {role.isActive
                          ? t('admin.roles.status.active')
                          : t('admin.roles.status.passive')}
                      </Tag>
                    </TableCell>
                    <TableCell className="w-12 px-2">
                      <RowActionsMenu
                        ariaLabel={t('admin.roles.action.openMenu', { name: role.name })}
                        items={[
                          {
                            key: 'edit',
                            label: t('admin.roles.action.edit'),
                            disabled: role.isSystemAdmin,
                            onSelect: () => {
                              setMessage(null);
                              setEditRole(role);
                            },
                          },
                          {
                            key: 'delete',
                            label: t('admin.roles.action.delete'),
                            destructive: true,
                            disabled: role.isSystemAdmin || !role.isActive,
                            onSelect: () => {
                              setMessage(null);
                              setDeleteRole(role);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('admin.pagination.nav'),
                previous: t('admin.pagination.previous'),
                next: t('admin.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <RoleFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => setMessage(t('admin.roles.created'))}
      />
      <RoleFormModal
        open={editRole !== null}
        onOpenChange={(open) => !open && setEditRole(null)}
        role={editRole}
        onSuccess={() => setMessage(t('admin.roles.updated'))}
      />
      <ConfirmDialog
        open={deleteRole !== null}
        onOpenChange={(open) => !open && setDeleteRole(null)}
        title={t('admin.roles.delete.title')}
        body={t('admin.roles.delete.body', { name: deleteRole?.name ?? '' })}
        confirmLabel={t('admin.roles.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => deleteRole && deleteMutation.mutate(deleteRole.uuid)}
      />
    </div>
  );
}
